import React from "react";
import { Snowflake } from "lucide-react";
import { COLOR } from "./pillars/_cards";

// Anchor points: coldest-typical-night °F → % range loss (AAA / Recurrent data)
const LOSS_CURVE = [[50, 5], [40, 10], [32, 15], [20, 23], [8, 30], [0, 34]];

const lossPctAt = (tempF) => {
  if (tempF >= LOSS_CURVE[0][0]) return LOSS_CURVE[0][1];
  for (let i = 0; i < LOSS_CURVE.length - 1; i++) {
    const [t1, l1] = LOSS_CURVE[i];
    const [t2, l2] = LOSS_CURVE[i + 1];
    if (tempF <= t1 && tempF >= t2) return l1 + ((t1 - tempF) / (t1 - t2)) * (l2 - l1);
  }
  return LOSS_CURVE[LOSS_CURVE.length - 1][1];
};

const hasHeatPump = (vehicle) => {
  const y = vehicle?.year || 2021;
  const m = vehicle?.model || "";
  if (m === "Cybertruck") return true;
  if (m === "Model Y") return true; // all MY builds
  return y >= 2021; // 3 refresh + S/X refresh
};

/** Winter Range Tax — what cold actually costs THIS car in THIS climate,
 *  and how much preconditioning claws back. */
export const WinterRangeTaxCard = ({ vehicle, climate }) => {
  const epa = vehicle?.epaRange || 300;
  const winterLow = climate?.winter_low_f ?? 26;
  const pump = hasHeatPump(vehicle);
  const rawLoss = lossPctAt(winterLow);
  const lossPct = +(rawLoss * (pump ? 0.72 : 1)).toFixed(0);
  const lossMi = Math.round((epa * lossPct) / 100);
  const recoverMi = Math.round(lossMi * 0.4);
  const mild = lossPct <= 8;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(127,209,255,0.28)", background: "rgba(127,209,255,0.04)" }}
      data-testid="winter-range-tax-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: `${COLOR.blue}18`, borderColor: `${COLOR.blue}55` }}
        >
          <Snowflake className="w-4 h-4" style={{ color: COLOR.blue }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Winter range tax
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            {climate?.label || "Your climate"} · {winterLow}°F typical low
          </div>
        </div>
        <div className="text-right shrink-0">
          <div
            className="font-display text-2xl font-semibold leading-none"
            style={{ color: mild ? COLOR.green : COLOR.blue }}
            data-testid="winter-loss-mi"
          >
            −{lossMi}<span className="font-mono text-[10px] text-zinc-400">mi</span>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
            −{lossPct}% on cold days
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/5">
        <div>
          <div className="font-display text-lg font-semibold leading-none" style={{ color: COLOR.green }}>
            +{recoverMi}<span className="font-mono text-[10px] text-zinc-400">mi</span>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
            recovered by preconditioning
          </div>
        </div>
        <div>
          <div className="font-display text-lg text-white font-semibold leading-none">
            {pump ? "Heat pump" : "Resistive"}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
            cabin heating
          </div>
        </div>
      </div>

      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed mt-3" data-testid="winter-tax-tip">
        {mild
          ? "Cold barely touches your range in this climate. Preconditioning while plugged in still saves the battery from heating the cabin."
          : "Precondition while plugged in before cold departures — cabin heat comes from the wall, not your pack, and the battery warms to its efficient window."}
      </p>
    </div>
  );
};
